import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ALLOWED_IMAGE_TYPE } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaSignedUrlModel } from '../../../../shared/interface/interface';
import { SharedModule } from '../../../../shared/shared.module';
import { UploadImageComponent } from '../../../../shared/components/upload-image/upload-image.component';
import { CustomEditorComponent } from '../../../../shared/components/custom-editor/custom-editor.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { ButtonLoaderComponent } from '../../../../shared/components/button-loader/button-loader.component';
import { InputChipsComponent } from '../../../../shared/components/input-chips/input-chips.component';
import { NewsService } from '../../../../shared/services/news.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-edit-news',
  standalone: true,
  imports: [
    SharedModule,
    UploadImageComponent,
    CustomEditorComponent,
    ButtonComponent,
    ButtonLoaderComponent,
    InputChipsComponent,
  ],
  templateUrl: './add-edit-news.component.html',
  styleUrl: './add-edit-news.component.scss',
})
export class AddEditNewsComponent {
  newsForm: FormGroup = this.fb.group({
    title: ['', [Validators.required]],
    description: ['', [Validators.required]],
    img: ['', [Validators.required]],
    publishedAt: [this.getFormattedDate(new Date()), [Validators.required]],
    tags: [[]],
  });

  editor!: any;

  subscription: Subscription = new Subscription();
  newsId: string | null = null;
  allowedImageType = ALLOWED_IMAGE_TYPE.join(',');
  imageUrl: string = '';
  isLoading = false;
  initialLoading = false;
  imageUploading = false;

  // private categoryFilterSubject$: BehaviorSubject<FilterModel> =
  //   new BehaviorSubject<FilterModel>(DEFAULT_FILTER);

  // categoryFilter = this.categoryFilterSubject$.asObservable();
  // categoryCount: number = 0;

  constructor(
    private readonly fb: FormBuilder,
    private readonly newsService: NewsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  init() {
    const sub = this.activatedRoute.paramMap.subscribe((data: any) => {
      this.newsId = data.get('id');
      this.setInititalNewsValue(this.newsId);
    });
    this.subscription.add(sub);
  }

  onSubmit() {
    if (
      !this.newsForm.valid ||
      this.isLoading ||
      this.initialLoading ||
      this.imageUploading
    ) {
      this.newsForm.markAllAsTouched();
      return;
    }
    const { title, description, img, publishedAt } = this.newsForm.value;
    const payload: any = {
      content: '',
      title,
      description,
      img,
      publishedAt: new Date(publishedAt).toISOString(),
    };
    if (this.newsId) {
      this.isLoading = true;
      this.newsService.updateNews(this.newsId, payload).subscribe({
        next: (res) => {
          this.toastr.success('News updated successfully!');
          this.router.navigate(['/news']);
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.isLoading = false;
        },
      });
    } else {
      this.isLoading = true;
      this.newsService.addNews(payload).subscribe({
        next: (res) => {
          this.toastr.success('News added successfully!');
          this.router.navigate(['/']);
          this.isLoading = false;
          this.cancel();
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.isLoading = false;
          console.error(err);
        },
      });
    }
    console.log(this.newsForm.value);
  }

  cancel() {
    this.router.navigate(['/news']);
  }

  setTagsValue(tags: string[]) {
    this.newsForm.controls['tags'].setValue(tags);
  }

  setInititalNewsValue(newsId: string | null) {
    if (newsId) {
      this.initialLoading = true;
      this.newsService.getNewsById(newsId).subscribe({
        next: (res: any) => {
          this.initialLoading = false;
          this.newsForm.patchValue({
            title: res?.title,
            description: res?.description,
            publishedAt: this.getFormattedDate(
              res?.publishedAt ? new Date(res?.publishedAt) : new Date()
            ),
            img: res?.key,
          });
          this.imageUrl = res?.img;
        },
        error: (err) => {
          console.error(err);
          this.router.navigate(['']);
        },
      });
    }
  }

  removeImage() {
    this.newsForm.get('img')?.setValue('');
    this.imageUrl = '';
  }

  onImageUploadStart() {
    this.imageUploading = true;
  }
  onImageUploadEnd(data: MediaSignedUrlModel | null) {
    this.imageUploading = false;
    if (data) {
      this.newsForm.get('img')?.setValue(data.key);
      this.imageUrl = data.contentUrl;
    }
  }

  getFormattedDate(publishDate: Date) {
    const month = (publishDate.getMonth() + 1).toString();
    const date = publishDate.getDate().toString();
    const hrs = publishDate.getHours().toString();
    const mins = publishDate.getMinutes().toString();
    return `${publishDate.getFullYear()}-${
      month.length > 1 ? month : '0' + month
    }-${date.length > 1 ? date : '0' + date}T${
      hrs.length > 1 ? hrs : '0' + hrs
    }:${mins.length > 1 ? mins : '0' + mins}`;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
